import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowRight, MapPin, Star, Tag } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader } from '../ui/card';
import { NamedLink } from '../../components';
import { cn } from '../ui/utils';
import { useConfiguration } from '../../context/configurationContext';
import { showListingThunk } from '../../containers/ListingPage/ListingPage.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import SellerBadges from '../SellerBadges/SellerBadges';

const { UUID } = sdkTypes;

type MarketplaceListingCardProps = {
  // Use href for a direct URL (e.g. mock/fallback data that can't use NamedLink).
  // Use listingId + listingSlug for real Sharetribe listings.
  href?: string;
  listingId?: string;
  listingSlug?: string;
  title: string;
  image: string;
  priceLabel: string;
  condition: string;
  location: string;
  category?: string;
  posted?: string;
  membershipTier?: number;
  isVerified?: boolean;
  sellerRating?: number;
  sellerReviews?: number;
  badgeText?: string;
  badgeClassName?: string;
  oldPriceLabel?: string;
  preloadSrc?: string;
  dataTestId?: string;
  titleTestId?: string;
  priceTestId?: string;
  className?: string;
};

function CardInner({
  title, image, priceLabel, condition, location, category, posted,
  membershipTier, isVerified, sellerRating, sellerReviews, badgeText, badgeClassName,
  oldPriceLabel, titleTestId, priceTestId,
}: Omit<MarketplaceListingCardProps, 'href' | 'listingId' | 'listingSlug' | 'dataTestId' | 'className'>) {
  return (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-muted">
        {image ? (
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full bg-muted" />
        )}
        {badgeText && (
          <div className="absolute left-2 top-2 z-10">
            <Badge
              className={cn(
                'border-none shadow-sm',
                badgeText === 'Hot'
                  ? 'gap-1 bg-yellow-400 text-yellow-900'
                  : 'bg-primary text-primary-foreground',
                badgeClassName
              )}
            >
              {badgeText === 'Hot' && <Star className="h-3 w-3 fill-current" />}
              {badgeText}
            </Badge>
          </div>
        )}
      </div>

      <CardHeader className="flex min-h-[7.5rem] flex-col p-3.5 pb-2.5">
        <div className="mb-1.5 min-h-[1rem] text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {category}
        </div>
        <h3
          className="line-clamp-2 min-h-[2.6rem] text-[1.25rem] font-heading font-bold leading-[1.08] tracking-tight text-foreground transition-colors group-hover:text-primary"
          data-testid={titleTestId}
        >
          {title}
        </h3>
        <div className="mt-auto flex min-h-[1.75rem] flex-wrap items-center gap-1.5 pt-2.5">
          <div className="text-[2rem] font-bold leading-none text-foreground" data-testid={priceTestId}>
            {priceLabel}
          </div>
          {oldPriceLabel && (
            <div className="pt-0.5 text-sm text-muted-foreground line-through">
              {oldPriceLabel}
            </div>
          )}
          {(membershipTier !== undefined || isVerified) && (
            <SellerBadges
              membershipTier={membershipTier ?? 0}
              isVerified={!!isVerified}
              size="compact"
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-grow p-3.5 pt-0">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
          <div className="flex min-w-0 items-center gap-1.5">
            <Tag className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="truncate">{condition}</span>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/75">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary/70" />
            <span className="truncate">{posted}</span>
          </div>
          {sellerRating !== undefined && sellerReviews !== undefined ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <Star className="h-3.5 w-3.5 shrink-0 fill-primary text-primary" />
              <span className="truncate">{sellerRating.toFixed(1)} · {sellerReviews} reviews</span>
            </div>
          ) : (
            <div />
          )}
          <div className="flex min-w-0 items-center justify-end gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="truncate text-right">{location}</span>
          </div>
        </div>
      </CardContent>
    </>
  );
}

function useCardHover(listingId?: string, preloadSrc?: string) {
  const dispatch = useDispatch();
  const config = useConfiguration() as any;

  return useCallback(() => {
    // Preload the card image via a <link rel="preload"> so the browser fetches it
    // before the user clicks through to the listing page.
    if (preloadSrc && typeof document !== 'undefined') {
      const id = `preload-${preloadSrc}`;
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'preload';
        link.as = 'image';
        link.href = preloadSrc;
        document.head.appendChild(link);
      }
    }
    // Prefetch the full listing entity (all image variants) into Redux so the
    // listing page can render images immediately on click without waiting for
    // its own showListing fetch to complete.
    if (listingId) {
      dispatch(showListingThunk({ listingId: new UUID(listingId), config }) as any);
    }
  }, [listingId, preloadSrc, dispatch, config]);
}

export function MarketplaceListingCard(props: MarketplaceListingCardProps) {
  const { href, listingId, listingSlug, preloadSrc, dataTestId, className, ...rest } = props;
  const handleMouseEnter = useCardHover(listingId, preloadSrc);
  const dispatch = useDispatch();
  const config = useConfiguration() as any;

  // Prefetch listing entity (with all listing-card image variants) so the
  // CheckoutPage thumbnail renders immediately after navigation.
  const prefetchListing = useCallback(() => {
    if (!listingId) return;
    dispatch(showListingThunk({ listingId: new UUID(listingId), config }) as any);
  }, [dispatch, config, listingId]);

  if (href) {
    return (
      <a
        href={href}
        className="block h-full group"
        data-testid={dataTestId}
        onMouseEnter={handleMouseEnter}
      >
        <Card className={cn('flex h-full flex-col overflow-hidden border-border transition-all duration-300 group-hover:border-primary/50 group-hover:shadow-lg', className)}>
          <CardInner {...rest} />
        </Card>
      </a>
    );
  }

  return (
    <div className="group h-full" onMouseEnter={handleMouseEnter} data-testid={dataTestId}>
      <Card className={cn('flex h-full flex-col overflow-hidden border-border transition-all duration-300 group-hover:border-primary/50 group-hover:shadow-lg', className)}>
        <NamedLink
          name="ListingPage"
          params={{ id: listingId, slug: listingSlug }}
          className="block"
        >
          <CardInner {...rest} />
        </NamedLink>
        {listingId && listingSlug && (
          <div className="px-3.5 pb-3.5 pt-1">
            <NamedLink
              name="CheckoutPage"
              params={{ slug: listingSlug, id: listingId }}
              className="group/buy flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-primary py-2.5 font-heading text-sm font-bold uppercase tracking-wide text-primary-foreground shadow-sm transition-all duration-200 hover:bg-foreground hover:shadow-md hover:-translate-y-0.5 active:scale-95 active:duration-75 motion-reduce:transition-none"
              onClick={prefetchListing}
            >
              <span className="transition-transform duration-200 group-hover/buy:-translate-x-1">
                Buy Now
              </span>
              <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-all duration-200 group-hover/buy:translate-x-0 group-hover/buy:opacity-100" />
            </NamedLink>
          </div>
        )}
      </Card>
    </div>
  );
}
